import { inject, Injectable } from '@angular/core';
import { Http, QueryHandlerFor, RootContext } from '@skylab-angular-shared/core';
import { CompanyParametersQuery, CurrencyCollectionQuery, PagedCollectionQuery, PagedCollectionQueryResult, PagedCollectionQueryResultDTO, UnitQuery, VatRateForSaleQuery } from '@skylab-shared/business';
import { HttpService, QueryHandler, RootContextBase, TaggedUrl, Tags } from '@skylab-shared/core';
import { InventoryLevelKind } from '@skylab-shared/dataModel';
import { Mutable } from '@skylab-shared/types';

export interface InventoryLevelGridItem {
    readonly id: number;
    readonly externalId: string;
    readonly version: string;
    readonly favourite: boolean;
    readonly productName: string;
    readonly unit: string;
    readonly quantity: number;
    readonly priceNet: number;
    readonly vatRateSymbol: string;
    readonly priceGross: number;
    readonly productActive: boolean;
    readonly currencySymbol: string;
    readonly currencyPrecision: number;
    readonly level: InventoryLevelKind;
    readonly symbol: string;
}

class InventoryLevelGridInternalItemDTO {
    public readonly active: boolean;
    public readonly currencyId: number;
    public readonly favourite: boolean;
    public readonly groupId: number;
    public readonly id: number;
    public readonly externalId: string;
    public readonly name: string;
    public readonly netPrice: number;
    public readonly vatRateId: number;
    public readonly grossPrice: number;
    public readonly warningLevel: number;
    public readonly quantity: number;
    public readonly unitId: number;
    public readonly version: string;
    public readonly symbol: string;
}

export class InventoryLevelGridQuery extends PagedCollectionQuery<InventoryLevelGridItem> { }

@Injectable({ providedIn: 'root'})
@QueryHandlerFor(InventoryLevelGridQuery)
export class InventoryLevelGridQueryHandler implements QueryHandler<InventoryLevelGridQuery, PagedCollectionQueryResult<InventoryLevelGridItem>> {
    #url = TaggedUrl.join('inventory/level', [Tags.Api]);
    #http = inject(Http) as HttpService;
    #rootContext = inject(RootContext) as any as RootContextBase;

    public async handleAsync(query: InventoryLevelGridQuery): Promise<PagedCollectionQueryResult<InventoryLevelGridItem>> {
        const { data } = await this.#http.get<Mutable<PagedCollectionQueryResult<InventoryLevelGridInternalItemDTO>>>(this.#url, {
            params: { ...query.params },
            dtoObject: new PagedCollectionQueryResultDTO<InventoryLevelGridInternalItemDTO>(InventoryLevelGridInternalItemDTO)
        });

        const { systemCurrency } = await this.#rootContext.getAsync(CompanyParametersQuery);
        const { precision: currencyPrecision } = await this.#rootContext.firstAsync(CurrencyCollectionQuery, { symbol: systemCurrency });

        const items = await Promise.all(data.items.map<Promise<InventoryLevelGridItem>>(async (item: InventoryLevelGridInternalItemDTO) => {
            const unit = await this.#rootContext.firstAsync(UnitQuery, { id: item.unitId });
            const vatRate = await this.#rootContext.firstAsync(VatRateForSaleQuery, { id: item.vatRateId });

            const mappedItem: InventoryLevelGridItem = {
                version: item.version,
                favourite: item.favourite,
                id: item.id,
                priceNet: item.netPrice,
                priceGross: item.grossPrice,
                productActive: item.active,
                vatRateSymbol: vatRate?.symbol,
                productName: item.name,
                quantity: item.quantity,
                unit: unit.symbol,
                currencySymbol: systemCurrency,
                currencyPrecision: currencyPrecision,
                level: this.getInventoryLevel(item),
                symbol: item.symbol,
                externalId: item.externalId
            };

            return mappedItem;
        }));

        return {
            items,
            totalItemsCount: data.totalItemsCount
        };
    }

    private getInventoryLevel(item: InventoryLevelGridInternalItemDTO): InventoryLevelKind {
        if (!item.quantity) {
            return InventoryLevelKind.Empty;
        } else if ((item.quantity && !item.warningLevel) || (item.quantity > item.warningLevel)) {
            return InventoryLevelKind.High;
        } else if (item.quantity <= item.warningLevel) {
            return InventoryLevelKind.Low;
        }

        return InventoryLevelKind.Empty;
    }
}
